import { createVNode as _createVNode, withCtx as _withCtx, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
  class: "flex items-center gap-0"
};
const _hoisted_2 = {
  class: "flex items-center gap-2"
};
const _hoisted_3 = {
  class: "flex items-center gap-2"
};
import { computed } from 'vue';
import { useMeStore } from '@/services/UserService/useMeStore';
import { storeToRefs } from 'pinia';
import LlTooltip from '@/components/LlTooltip.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { getShortcutTooltip } from '@/constants/keyboardShortcuts';
export default {
  __name: 'EntryActions',
  props: {
    editEntryState: {
      type: Object,
      required: true
    },
    entryChanged: {
      type: Boolean,
      required: true
    },
    formValid: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    isModal: {
      type: Boolean,
      default: false
    },
    isTimerConversion: {
      type: Boolean,
      default: false
    },
    hideDuplicateButton: {
      type: Boolean,
      default: false
    },
    tracking: {
      type: Object,
      required: false
    }
  },
  emits: ['close', 'createEntry', 'duplicateEntry', 'deleteDialogOpen'],
  setup(__props, {
    emit: __emit
  }) {
    const {
      permissions,
      me,
      features
    } = storeToRefs(useMeStore());
    const props = __props;
    const emit = __emit;

    // Computed property to determine if save button should be disabled
    // This handles edge cases during form restoration and panel ↔ modal transitions
    const isSaveDisabled = computed(() => {
      const {
        loading,
        formValid,
        entryChanged,
        editEntryState,
        isTimerConversion
      } = props;

      // CRITICAL: During form restoration (panel ↔ modal transitions), don't disable save button
      // The formRestorationInProgress flag indicates a temporary state that shouldn't block saving
      // This applies to both new AND existing entries during transitions
      if (editEntryState.formRestorationInProgress) {
        return false;
      }

      // Basic loading and validation checks
      if (loading || !formValid) {
        return true;
      }

      // CRITICAL: For new entries (no entryId), be more lenient with change detection
      // New entries should be savable as long as they're valid, regardless of "changed" state
      const isNewEntry = !editEntryState.entryId;
      if (isNewEntry) {
        // For new entries, only check if it's a duplicate or timer conversion that needs special handling
        // Don't require entryChanged to be true for regular new entries
        return false;
      }

      // For existing entries, use standard change detection
      // BUT allow saving during special scenarios (duplicates, timer conversions)
      // ADDITIONAL: Also check if we have meaningful form data even if entryChanged is false
      // This helps with edge cases during panel ↔ modal transitions
      const hasChanges = entryChanged || editEntryState.duplicate || isTimerConversion;
      return !hasChanges;
    });
    const canShowDeleteButton = computed(() => {
      const {
        editEntryState
      } = props;
      return !editEntryState.duplicate && editEntryState.entryId &&
      // Only check entryId for existing entries
      !editEntryState.form.billed && (me.value?.id === editEntryState.form.account?.id && permissions.value?.time?.createSelf || permissions.value?.time?.createOthers);
    });
    const canShowDuplicateButton = computed(() => {
      const {
        editEntryState,
        hideDuplicateButton
      } = props;
      // Don't show duplicate button if explicitly hidden, for new entries (no entryId), or when duplicating
      if (hideDuplicateButton) return false;
      return !editEntryState.duplicate && editEntryState.entryId;
    });
    const duplicateEntry = () => {
      emit('duplicateEntry', props.editEntryState.form.id);
    };

    // Helper function to track time_entry_action events according to requirements
    const trackTimeEntryAction = (action, method = 'button_click') => {
      if (props.tracking?.event) {
        props.tracking.event('time_entry_action', '', {
          action: action,
          // "save" | "save_and_new" | "new_entry" | "cancel"
          method: method,
          // "shortcut" | "button_click" | "tab_focus_enter"
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
      }
    };

    // Handle tab focus + enter key detection
    const handleTabFocusEnter = (action, event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        if (features.value?.enableTimeEntryShortcuts) {
          trackTimeEntryAction(action, 'tab_focus_enter');
        }

        // Trigger the appropriate action
        switch (action) {
          case 'save':
            createEntry();
            break;
          case 'save_and_new':
            createEntry(false, true);
            break;
          case 'cancel':
            closeModal();
            break;
        }
      }
    };
    const createEntry = (keepTimer = false, bulkEntry = false) => {
      emit('createEntry', keepTimer, bulkEntry);
    };
    const deleteDialogOpen = () => {
      emit('deleteDialogOpen');
    };
    const closeModal = () => {
      emit('close');
    };
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", {
        class: _normalizeClass(["bottom-0 flex justify-center p-0 at-save-update-btn bg-white", {
          'max-w-[22rem]': !__props.isModal,
          'w-full': __props.isModal
        }])
      }, [_createElementVNode("div", {
        class: _normalizeClass(["flex items-center gap-2 p-4 w-full px-0", {
          'justify-between': canShowDeleteButton.value || canShowDuplicateButton.value || !canShowDeleteButton.value && !canShowDuplicateButton.value,
          'justify-end': false
        }])
      }, [_withDirectives(_createElementVNode("div", _hoisted_1, [_withDirectives(_createElementVNode("div", null, [_createVNode(LlTooltip, {
        text: "Delete",
        location: "top"
      }, {
        activator: _withCtx(() => [_createVNode(LlSecondaryButton, {
          icon: "mdi-delete",
          variant: "text",
          color: "error",
          "aria-label": "Delete time entry",
          disabled: props.loading,
          size: __props.isModal ? 'default' : 'small',
          onClick: _cache[0] || (_cache[0] = $event => deleteDialogOpen())
        }, null, 8, ["disabled", "size"])]),
        _: 1
      })], 512), [[_vShow, canShowDeleteButton.value]]), _withDirectives(_createElementVNode("div", null, [_createVNode(LlTooltip, {
        text: "Duplicate",
        location: "top"
      }, {
        activator: _withCtx(() => [_createVNode(LlSecondaryButton, {
          icon: "mdi-content-copy",
          variant: "text",
          "aria-label": "Duplicate time entry",
          disabled: props.loading,
          size: __props.isModal ? 'default' : 'small',
          "data-cy": "duplicate-entry-button",
          onClick: _cache[1] || (_cache[1] = $event => duplicateEntry())
        }, null, 8, ["disabled", "size"])]),
        _: 1
      })], 512), [[_vShow, canShowDuplicateButton.value]])], 512), [[_vShow, (canShowDeleteButton.value || canShowDuplicateButton.value) && !__props.isModal]]), _withDirectives(_createElementVNode("div", null, [_unref(features)?.enableTimeEntryShortcuts ? (_openBlock(), _createBlock(LlTooltip, {
        key: 0,
        text: _unref(getShortcutTooltip)('cancel', 'Cancel'),
        location: "top"
      }, {
        activator: _withCtx(({
          props: tooltipProps
        }) => [_createVNode(LlSecondaryButton, _mergeProps(tooltipProps, {
          variant: "outlined",
          onClick: _cache[2] || (_cache[2] = () => {
            if (_unref(features)?.enableTimeEntryShortcuts) trackTimeEntryAction('cancel', 'button_click');
            closeModal();
          }),
          onKeydown: _cache[3] || (_cache[3] = event => handleTabFocusEnter('cancel', event)),
          disabled: props.loading,
          size: __props.isModal ? 'default' : 'small',
          class: "flex-shrink-0",
          "data-cy": "cancel-button"
        }), {
          default: _withCtx(() => [_createTextVNode(_toDisplayString(__props.editEntryState.form.billed ? 'Done' : 'Cancel'), 1)]),
          _: 1
        }, 16, ["disabled", "size"])]),
        _: 1
      }, 8, ["text"])) : (_openBlock(), _createBlock(LlSecondaryButton, {
        key: 1,
        variant: "outlined",
        onClick: _cache[4] || (_cache[4] = () => {
          if (_unref(features)?.enableTimeEntryShortcuts) trackTimeEntryAction('cancel', 'button_click');
          closeModal();
        }),
        onKeydown: _cache[5] || (_cache[5] = event => handleTabFocusEnter('cancel', event)),
        disabled: props.loading,
        size: __props.isModal ? 'default' : 'small',
        class: "flex-shrink-0",
        "data-cy": "cancel-button"
      }, {
        default: _withCtx(() => [_createTextVNode(_toDisplayString(__props.editEntryState.form.billed ? 'Done' : 'Cancel'), 1)]),
        _: 1
      }, 8, ["disabled", "size"]))], 512), [[_vShow, !canShowDeleteButton.value && !canShowDuplicateButton.value]]), _withDirectives(_createElementVNode("div", _hoisted_2, [_withDirectives(_createVNode(LlSecondaryButton, {
        variant: "outlined",
        color: "error",
        disabled: props.loading,
        onClick: _cache[6] || (_cache[6] = $event => deleteDialogOpen()),
        size: __props.isModal ? 'default' : 'small',
        class: "flex-shrink-0"
      }, {
        default: _withCtx(() => [...(_cache[25] || (_cache[25] = [_createTextVNode(" Delete ", -1)]))]),
        _: 1
      }, 8, ["disabled", "size"]), [[_vShow, canShowDeleteButton.value]]), _withDirectives(_createVNode(LlSecondaryButton, {
        variant: "outlined",
        disabled: props.loading,
        onClick: _cache[7] || (_cache[7] = $event => duplicateEntry()),
        size: __props.isModal ? 'default' : 'small',
        class: "flex-shrink-0"
      }, {
        default: _withCtx(() => [...(_cache[26] || (_cache[26] = [_createTextVNode(" Duplicate ", -1)]))]),
        _: 1
      }, 8, ["disabled", "size"]), [[_vShow, canShowDuplicateButton.value]]), _unref(features)?.enableTimeEntryShortcuts ? (_openBlock(), _createBlock(LlTooltip, {
        key: 0,
        text: _unref(getShortcutTooltip)('cancel', 'Cancel'),
        location: "top"
      }, {
        activator: _withCtx(({
          props: tooltipProps
        }) => [_createVNode(LlSecondaryButton, _mergeProps(tooltipProps, {
          variant: "outlined",
          onClick: _cache[8] || (_cache[8] = () => {
            if (_unref(features)?.enableTimeEntryShortcuts) trackTimeEntryAction('cancel', 'button_click');
            closeModal();
          }),
          onKeydown: _cache[9] || (_cache[9] = event => handleTabFocusEnter('cancel', event)),
          disabled: props.loading,
          size: __props.isModal ? 'default' : 'small',
          class: "flex-shrink-0",
          "data-cy": "cancel-button"
        }), {
          default: _withCtx(() => [_createTextVNode(_toDisplayString(__props.editEntryState.form.billed ? 'Done' : 'Cancel'), 1)]),
          _: 1
        }, 16, ["disabled", "size"])]),
        _: 1
      }, 8, ["text"])) : (_openBlock(), _createBlock(LlSecondaryButton, {
        key: 1,
        variant: "outlined",
        onClick: _cache[10] || (_cache[10] = () => {
          if (_unref(features)?.enableTimeEntryShortcuts) trackTimeEntryAction('cancel', 'button_click');
          closeModal();
        }),
        onKeydown: _cache[11] || (_cache[11] = event => handleTabFocusEnter('cancel', event)),
        disabled: props.loading,
        size: __props.isModal ? 'default' : 'small',
        class: "flex-shrink-0",
        "data-cy": "cancel-button"
      }, {
        default: _withCtx(() => [_createTextVNode(_toDisplayString(__props.editEntryState.form.billed ? 'Done' : 'Cancel'), 1)]),
        _: 1
      }, 8, ["disabled", "size"]))], 512), [[_vShow, __props.isModal && (canShowDeleteButton.value || canShowDuplicateButton.value)]]), _createElementVNode("div", _hoisted_3, [_unref(features)?.enableTimeEntryShortcuts ? (_openBlock(), _createBlock(LlTooltip, {
        key: 0,
        text: _unref(getShortcutTooltip)('cancel', 'Cancel'),
        location: "top"
      }, {
        activator: _withCtx(({
          props: tooltipProps
        }) => [_withDirectives(_createVNode(LlSecondaryButton, _mergeProps(tooltipProps, {
          variant: "outlined",
          onClick: _cache[12] || (_cache[12] = () => {
            if (_unref(features)?.enableTimeEntryShortcuts) trackTimeEntryAction('cancel', 'button_click');
            closeModal();
          }),
          onKeydown: _cache[13] || (_cache[13] = event => handleTabFocusEnter('cancel', event)),
          disabled: props.loading,
          size: __props.isModal ? 'default' : 'small',
          class: ["flex-shrink-0", {
            'mr-16': __props.editEntryState.form.billed
          }],
          "data-cy": "cancel-button"
        }), {
          default: _withCtx(() => [_createTextVNode(_toDisplayString(__props.editEntryState.form.billed ? 'Done' : 'Cancel'), 1)]),
          _: 1
        }, 16, ["disabled", "size", "class"]), [[_vShow, (canShowDeleteButton.value || canShowDuplicateButton.value) && !__props.isModal]])]),
        _: 1
      }, 8, ["text"])) : _withDirectives((_openBlock(), _createBlock(LlSecondaryButton, {
        key: 1,
        variant: "outlined",
        onClick: _cache[14] || (_cache[14] = () => {
          if (_unref(features)?.enableTimeEntryShortcuts) trackTimeEntryAction('cancel', 'button_click');
          closeModal();
        }),
        onKeydown: _cache[15] || (_cache[15] = event => handleTabFocusEnter('cancel', event)),
        disabled: props.loading,
        size: __props.isModal ? 'default' : 'small',
        class: _normalizeClass(["flex-shrink-0", {
          'mr-16': __props.editEntryState.form.billed
        }]),
        "data-cy": "cancel-button"
      }, {
        default: _withCtx(() => [_createTextVNode(_toDisplayString(__props.editEntryState.form.billed ? 'Done' : 'Cancel'), 1)]),
        _: 1
      }, 8, ["disabled", "size", "class"])), [[_vShow, (canShowDeleteButton.value || canShowDuplicateButton.value) && !__props.isModal]]), _withDirectives(_createVNode(LlSecondaryButton, {
        variant: "flat",
        disabled: isSaveDisabled.value,
        onClick: _cache[16] || (_cache[16] = _withModifiers($event => createEntry(true), ["prevent"])),
        size: __props.isModal ? 'default' : 'small',
        class: "flex-shrink-0",
        color: "primary"
      }, {
        default: _withCtx(() => [...(_cache[27] || (_cache[27] = [_createTextVNode(" Save & Keep Timer ", -1)]))]),
        _: 1
      }, 8, ["disabled", "size"]), [[_vShow, __props.editEntryState.showKeepTimer && !__props.editEntryState.form.billed]]), _unref(features)?.enableTimeEntryShortcuts ? (_openBlock(), _createBlock(LlTooltip, {
        key: 2,
        text: _unref(getShortcutTooltip)('save_and_new', 'Save and New'),
        location: "top"
      }, {
        activator: _withCtx(({
          props: tooltipProps
        }) => [_withDirectives(_createVNode(LlSecondaryButton, _mergeProps(tooltipProps, {
          variant: "flat",
          disabled: isSaveDisabled.value,
          onClick: _cache[17] || (_cache[17] = _withModifiers(() => {
            trackTimeEntryAction('save_and_new', 'button_click');
            createEntry(false, true);
          }, ["prevent"])),
          onKeydown: _cache[18] || (_cache[18] = event => handleTabFocusEnter('save_and_new', event)),
          size: __props.isModal ? 'default' : 'small',
          class: "flex-shrink-0",
          color: "primary",
          "data-cy": "save-and-new"
        }), {
          default: _withCtx(() => [...(_cache[28] || (_cache[28] = [_createTextVNode(" Save and New ", -1)]))]),
          _: 1
        }, 16, ["disabled", "size"]), [[_vShow, __props.editEntryState.isBulkCreate && !__props.editEntryState.entryId]])]),
        _: 1
      }, 8, ["text"])) : _withDirectives((_openBlock(), _createBlock(LlSecondaryButton, {
        key: 3,
        variant: "flat",
        disabled: isSaveDisabled.value,
        onClick: _cache[19] || (_cache[19] = _withModifiers($event => createEntry(false, true), ["prevent"])),
        onKeydown: _cache[20] || (_cache[20] = event => handleTabFocusEnter('save_and_new', event)),
        size: __props.isModal ? 'default' : 'small',
        class: "flex-shrink-0",
        color: "primary",
        "data-cy": "save-and-new"
      }, {
        default: _withCtx(() => [...(_cache[29] || (_cache[29] = [_createTextVNode(" Save and New ", -1)]))]),
        _: 1
      }, 8, ["disabled", "size"])), [[_vShow, __props.editEntryState.isBulkCreate && !__props.editEntryState.entryId]]), _unref(features)?.enableTimeEntryShortcuts ? (_openBlock(), _createBlock(LlTooltip, {
        key: 4,
        text: _unref(getShortcutTooltip)('save', 'Save'),
        location: "top"
      }, {
        activator: _withCtx(({
          props: tooltipProps
        }) => [_withDirectives(_createVNode(LlSecondaryButton, _mergeProps(tooltipProps, {
          variant: "flat",
          color: "primary",
          disabled: isSaveDisabled.value,
          onClick: _cache[21] || (_cache[21] = _withModifiers(() => {
            trackTimeEntryAction('save', 'button_click');
            createEntry();
          }, ["prevent"])),
          onKeydown: _cache[22] || (_cache[22] = event => handleTabFocusEnter('save', event)),
          size: __props.isModal ? 'default' : 'small',
          class: ["flex-shrink-0", {
            'mr-16': !__props.isModal
          }],
          "data-cy": "save-entry-button"
        }), {
          default: _withCtx(() => [...(_cache[30] || (_cache[30] = [_createTextVNode(" Save ", -1)]))]),
          _: 1
        }, 16, ["disabled", "size", "class"]), [[_vShow, !__props.editEntryState.form.billed]])]),
        _: 1
      }, 8, ["text"])) : _withDirectives((_openBlock(), _createBlock(LlSecondaryButton, {
        key: 5,
        variant: "flat",
        color: "primary",
        disabled: isSaveDisabled.value,
        onClick: _cache[23] || (_cache[23] = _withModifiers($event => createEntry(), ["prevent"])),
        onKeydown: _cache[24] || (_cache[24] = event => handleTabFocusEnter('save', event)),
        size: __props.isModal ? 'default' : 'small',
        class: _normalizeClass(["flex-shrink-0", {
          'mr-16': !__props.isModal
        }]),
        "data-cy": "save-entry-button"
      }, {
        default: _withCtx(() => [...(_cache[31] || (_cache[31] = [_createTextVNode(" Save ", -1)]))]),
        _: 1
      }, 8, ["disabled", "size", "class"])), [[_vShow, !__props.editEntryState.form.billed]])])], 2)], 2);
    };
  }
};